// Concurrent reverse geocoding helper.
// Goals:
// 1. Run Google JS Geocoder and REST API (if key) in parallel.
// 2. Return first high quality human-readable label (street + city/state OR city/state OR country).
// 3. NEVER return raw lat/lng as a user-facing fallback (dev policy: surface errors instead of silent fallbacks).
// 4. Provide structured parts for future enrichment (whereMet, whenMet already handled elsewhere).
// 5. Hard timeout to avoid hanging UI.

export interface ReverseGeocodeResult {
    label: string | null;        // Human readable (null if failed)
    city?: string | null;
    state?: string | null;
    country?: string | null;
    street?: string | null;
    source: 'js' | 'rest' | 'none';
    error?: string;              // Present when label null
    latencyMs: number;
}

interface AddressParts {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
}

function extractParts(components: any[]): AddressParts {
    const get = (t: string, short = false) => {
        const c = components.find((c: any) => c.types?.includes(t));
        if (!c) return '';
        return short ? (c.short_name || c.long_name || '') : (c.long_name || c.short_name || '');
    };
    const streetNum = get('street_number');
    const route = get('route');
    const street = [streetNum, route].filter(Boolean).join(' ').trim();
    const city = get('locality') || get('postal_town');
    const state = get('administrative_area_level_1', true);
    const country = get('country', true);
    return { street: street || undefined, city: city || undefined, state: state || undefined, country: country || undefined };
}

function buildLabel(parts: AddressParts, formattedFallback?: string): string | null {
    if (parts.street && parts.city && parts.state) return `${parts.street}, ${parts.city}, ${parts.state}`;
    if (parts.city && parts.state) return `${parts.city}, ${parts.state}`;
    if (parts.city && parts.country) return `${parts.city}, ${parts.country}`;
    if (parts.state && parts.country) return `${parts.state}, ${parts.country}`;
    if (parts.country) return parts.country;
    if (formattedFallback) return formattedFallback.split(',').slice(0, 2).join(', ');
    return null;
}

async function jsGeocode(lat: number, lng: number): Promise<Omit<ReverseGeocodeResult, 'latencyMs'>> {
    if (!(window as any).google?.maps?.Geocoder) {
        return { label: null, source: 'none', error: 'Google Maps JS API not loaded' };
    }
    try {
        const geocoder = new (window as any).google.maps.Geocoder();
        const results: any[] = await new Promise((resolve, reject) => {
            geocoder.geocode({ location: { lat, lng } }, (res: any[], status: any) => {
                if (status !== (window as any).google.maps.GeocoderStatus.OK || !res?.length) {
                    resolve([]);
                    return;
                }
                resolve(res);
            });
        });
        if (!results.length) return { label: null, source: 'js', error: 'No results' };
        const best = results.find(r => r.types?.includes('street_address')) || results.find(r => r.types?.includes('premise')) || results[0];
        const parts = extractParts(best.address_components || []);
        const label = buildLabel(parts, best.formatted_address);
        return { label, ...parts, source: 'js', error: label ? undefined : 'Could not build label' };
    } catch (e: any) {
        return { label: null, source: 'js', error: e?.message || 'Geocoder error' };
    }
}

async function restGeocode(lat: number, lng: number, apiKey?: string): Promise<Omit<ReverseGeocodeResult, 'latencyMs'>> {
    if (!apiKey) return { label: null, source: 'rest', error: 'No API key' };
    try {
        const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
        if (!resp.ok) return { label: null, source: 'rest', error: `HTTP ${resp.status}` };
        const data = await resp.json();
        if (!data.results?.length) return { label: null, source: 'rest', error: 'No results' };
        const best = data.results.find((r: any) => r.types?.includes('street_address')) || data.results[0];
        const parts = extractParts(best.address_components || []);
        const label = buildLabel(parts, best.formatted_address);
        return { label, ...parts, source: 'rest', error: label ? undefined : 'Could not build label' };
    } catch (e: any) {
        return { label: null, source: 'rest', error: e?.message || 'REST error' };
    }
}

export async function reverseGeocodeConcurrent(lat: number, lng: number, opts?: { timeoutMs?: number }): Promise<ReverseGeocodeResult> {
    const start = performance.now();
    const timeoutMs = opts?.timeoutMs ?? 6000;
    const apiKey = (import.meta as any)?.env?.VITE_GOOGLE_MAPS_API_KEY || (import.meta as any)?.env?.VITE_GOOGLE_API_KEY;

    const timerPromise = new Promise<Omit<ReverseGeocodeResult, 'latencyMs'>>((resolve) => {
        setTimeout(() => resolve({ label: null, source: 'none', error: 'Timeout' }), timeoutMs);
    });

    const jsPromise = jsGeocode(lat, lng);
    const restPromise = restGeocode(lat, lng, apiKey);

    // Wait for all to settle OR early resolve if one yields a label.
    return new Promise<ReverseGeocodeResult>((resolve) => {
        let resolved = false;
        const finish = (res: Omit<ReverseGeocodeResult, 'latencyMs'>) => {
            if (resolved) return; // first win or timeout
            if (res.label || res.source === 'none') {
                resolved = true;
                resolve({ ...res, latencyMs: Math.round(performance.now() - start) });
            }
        };

        jsPromise.then(r => { if (r.label) finish(r); });
        restPromise.then(r => { if (r.label) finish(r); });
        timerPromise.then(r => finish(r));

        // Final collector after all complete if none returned label yet.
        Promise.allSettled([jsPromise, restPromise]).then((all) => {
            if (resolved) return;
            const vals = all.map(a => a.status === 'fulfilled' ? a.value as Omit<ReverseGeocodeResult, 'latencyMs'> : { label: null, source: 'none', error: 'rejected' });
            // Pick one with most fields
            const scored = vals.map(v => ({ v, score: (v.label ? 10 : 0) + (v.city ? 2 : 0) + (v.state ? 2 : 0) + (v.street ? 1 : 0) }));
            scored.sort((a, b) => b.score - a.score);
            const best = scored[0].v;
            resolved = true;
            resolve({ ...best, latencyMs: Math.round(performance.now() - start) });
        });
    });
}
